import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Box, Download, RotateCw, Pause, ZoomIn, ZoomOut, Mic } from "lucide-react";
import api from "@/lib/api";

export function loadModelViewer() {
  if (document.querySelector('script[data-model-viewer]')) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.type = "module";
    s.src = "https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js";
    s.dataset.modelViewer = "true";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export function useAuthImage(assetId) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    let mounted = true;
    if (!assetId) return undefined;
    api.get(`/creators/forge/assets/${assetId}/image`, { responseType: "blob" })
      .then((r) => {
        if (!mounted) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => { mounted = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [assetId]);
  return src;
}

export function AssetImage({ assetId, alt = "", className = "", testid }) {
  const src = useAuthImage(assetId);
  if (!src) {
    return <div className={`bg-black/60 grid-texture flex items-center justify-center ${className}`}><Loader2 className="w-4 h-4 text-nc-blue animate-spin" /></div>;
  }
  return <img data-testid={testid} src={src} alt={alt} className={className} />;
}

export default function GlbPreview({ jobId, endpoint = "/creators/forge/jobs", className = "aspect-square" }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [rotating, setRotating] = useState(true);
  const viewerRef = useRef(null);
  const orbit = useRef({ theta: 45, phi: 75, radius: 2.2 });

  useEffect(() => {
    let objectUrl = null;
    let mounted = true;
    loadModelViewer()
      .then(() => api.get(`${endpoint}/${jobId}/glb`, { responseType: "blob" }))
      .then((r) => {
        if (!mounted) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => { mounted = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [jobId, endpoint]);

  const applyOrbit = () => {
    const el = viewerRef.current;
    if (!el) return;
    const { theta, phi, radius } = orbit.current;
    el.setAttribute("camera-orbit", `${theta}deg ${phi}deg ${radius}m`);
  };

  const zoom = (factor) => {
    orbit.current.radius = Math.min(8, Math.max(0.6, orbit.current.radius * factor));
    applyOrbit();
  };

  const toggleRotate = () => setRotating((r) => !r);

  // Voice commands + external control bus: "rotate", "zoom in", "zoom out"
  useEffect(() => {
    const onCmd = (e) => {
      const cmd = e.detail;
      if (cmd === "rotate") toggleRotate();
      else if (cmd === "zoom-in") zoom(0.75);
      else if (cmd === "zoom-out") zoom(1.35);
    };
    window.addEventListener("forge-viewer-cmd", onCmd);
    return () => window.removeEventListener("forge-viewer-cmd", onCmd);
  }, []);

  useEffect(() => {
    const el = viewerRef.current;
    if (el) {
      if (rotating) el.setAttribute("auto-rotate", ""); else el.removeAttribute("auto-rotate");
    }
  }, [rotating, src]);

  const voiceOn = (() => {
    try { return !!JSON.parse(localStorage.getItem("sp_a11y") || "{}").voice; } catch { return false; }
  })();

  if (failed) {
    return <div className={`border border-red-500/30 bg-black/60 flex items-center justify-center ${className}`}><p className="font-mono text-[9px] text-red-400 uppercase">Preview failed — download instead</p></div>;
  }
  if (!src) {
    return (
      <div className={`border border-white/10 bg-black/60 grid-texture flex flex-col items-center justify-center gap-3 ${className}`}>
        <Loader2 className="w-6 h-6 text-nc-blue animate-spin" />
        <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em]">Loading model…</p>
      </div>
    );
  }
  return (
    <div data-testid={`glb-preview-${jobId}`} className={`relative border border-nc-blue/30 bg-[#0a0d14] ${className}`}>
      <model-viewer ref={viewerRef} src={src} camera-controls auto-rotate interpolation-decay="200" camera-orbit="45deg 75deg 2.2m" shadow-intensity="1" style={{ width: "100%", height: "100%" }} />
      <div className="absolute top-2 right-2 flex flex-col gap-1.5">
        <button data-testid={`glb-rotate-${jobId}`} aria-label={rotating ? "Pause rotation" : "Rotate model"} onClick={toggleRotate} className="border border-white/20 bg-black/70 p-2 text-zinc-300 hover:text-white hover:border-nc-blue transition-colors">
          {rotating ? <Pause className="w-3.5 h-3.5" /> : <RotateCw className="w-3.5 h-3.5" />}
        </button>
        <button data-testid={`glb-zoom-in-${jobId}`} aria-label="Zoom in" onClick={() => zoom(0.75)} className="border border-white/20 bg-black/70 p-2 text-zinc-300 hover:text-white hover:border-nc-blue transition-colors">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button data-testid={`glb-zoom-out-${jobId}`} aria-label="Zoom out" onClick={() => zoom(1.35)} className="border border-white/20 bg-black/70 p-2 text-zinc-300 hover:text-white hover:border-nc-blue transition-colors">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
      </div>
      {voiceOn && (
        <p data-testid={`glb-voice-hint-${jobId}`} role="status" className="absolute bottom-2 left-2 font-mono text-[9px] text-nc-blue bg-black/70 border border-nc-blue/40 px-2 py-1 flex items-center gap-1.5">
          <Mic className="w-3 h-3" /> Say "rotate", "zoom in", "zoom out", "next model" or "close viewer"
        </p>
      )}
    </div>
  );
}

export function GlbDownload({ jobId, endpoint = "/creators/forge/jobs" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`${endpoint}/${jobId}/glb`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `forge-${jobId.slice(0, 8)}.glb`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed — model not ready");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={`glb-download-${jobId}`} disabled={busy} onClick={download} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-green/50 text-nc-green hover:bg-nc-green/10 disabled:opacity-40 transition-colors flex items-center gap-1.5">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} GLB
    </button>
  );
}
